// Run-scoped harness transport — carved from `src/features/agent-harness/client.ts`
// (ARCFE-1059 · D1). Transport comes from the `@heidi/api` configured-client
// registry (E5, ARCFE-805); bodies are otherwise identical to the pre-move
// source, `scribeHttpV2.` -> `http()`.
//
// A MIXED CARVE, deliberately. `RunTransport` has nine members and only these
// six are pure HTTP over the run paths. The other three — `streamPath`,
// `eventsPath`, `streamHeaders` — resolve the ml-scribe origin
// (`@/helpers/getDomains`) and mint auth/identity headers
// (`@/utils/http/clientIdentityHeaders`, `getAuthTokenForHttpClientSync`),
// which are app and platform state a headless package may not hold. So the app
// keeps `client.ts` as the ASSEMBLY: it imports these five, adds the three it
// owns, and exports the same `agentHarnessClient` object with the same shape.
// That is the commerce mixed-shim precedent, not a seam — nothing is injected.
import { getScribeHttpClients } from '@heidi/api/client';

import type {
  ApprovalMode,
  AttachmentDownloadUrlResponse,
  CancelTurnReason,
  PublicRunSnapshot,
  ResolveApprovalRequest,
  ResolveApprovalResponse,
  SubmitTurnRequest,
  SubmitTurnResponse,
} from '../../contracts/harness.types';
import {
  agentHarnessAttachmentContentPath,
  agentHarnessAttachmentDownloadUrlPath,
  agentHarnessRunApprovalModePath,
  agentHarnessRunApprovalResolvePath,
  agentHarnessRunCancelPath,
  agentHarnessRunPath,
  agentHarnessRunTurnsPath,
} from '../../contracts/paths';

interface ScribeHttpRequestConfig {
  responseType?: 'arraybuffer' | 'blob';
}

interface ScribeHttpResponse<T> {
  data: T;
  headers?: unknown;
}

interface ScribeHttp {
  get<T>(url: string, config?: ScribeHttpRequestConfig): Promise<ScribeHttpResponse<T>>;
  post<T>(url: string, body?: unknown): Promise<{ data: T }>;
  patch<T>(url: string, body?: unknown): Promise<{ data: T }>;
}

const http = (): ScribeHttp => getScribeHttpClients().scribeHttpV2;

export const getRun = async (agentRunId: string): Promise<PublicRunSnapshot> => {
  const response = await http().get<PublicRunSnapshot>(agentHarnessRunPath(agentRunId));
  return response.data;
};

export const submitTurn = async (
  agentRunId: string,
  body: SubmitTurnRequest
): Promise<SubmitTurnResponse> => {
  const response = await http().post<SubmitTurnResponse>(
    agentHarnessRunTurnsPath(agentRunId),
    body
  );
  return response.data;
};

export const cancelTurn = async (
  agentRunId: string,
  reason?: CancelTurnReason
): Promise<void> => {
  await http().post(agentHarnessRunCancelPath(agentRunId), reason ? { reason } : {});
};

// Applies from the run's next tool call, mid-turn.
export const setAgentRunApprovalMode = async (
  agentRunId: string,
  approvalMode: ApprovalMode
): Promise<ApprovalMode> => {
  const response = await http().patch<{ approval_mode: ApprovalMode }>(
    agentHarnessRunApprovalModePath(agentRunId),
    { approval_mode: approvalMode }
  );
  return response.data.approval_mode;
};

export const resolveApproval = async (
  agentRunId: string,
  approvalId: string,
  body: ResolveApprovalRequest
): Promise<ResolveApprovalResponse> => {
  const response = await http().post<ResolveApprovalResponse>(
    agentHarnessRunApprovalResolvePath(agentRunId, approvalId),
    body
  );
  return response.data;
};

const previewFailure = (error: unknown): Error => {
  const response =
    typeof error === 'object' && error !== null && 'response' in error
      ? (error as { response?: { status?: unknown } }).response
      : undefined;
  const status = typeof response?.status === 'number' ? response.status : null;
  return new Error(
    status === null
      ? 'Attachment preview failed'
      : `Attachment preview failed (${status})`
  );
};

const mediaTypeFromHeaders = (headers: unknown): string => {
  let raw = '';
  if (headers && typeof headers === 'object' && 'get' in headers) {
    const get = (headers as { get?: (name: string) => unknown }).get;
    if (typeof get === 'function') {
      const value = get.call(headers, 'content-type');
      if (typeof value === 'string') raw = value;
    }
  }
  if (!raw && headers && typeof headers === 'object') {
    const record = headers as Record<string, unknown>;
    const value = record['content-type'] ?? record['Content-Type'];
    if (typeof value === 'string') raw = value;
  }
  const type = raw.split(';', 1)[0]?.trim() ?? '';
  if (!type || /[\s\r\n]/.test(type)) return '';
  return type;
};

/** Copy the body into a Blob in this realm. An ArrayBuffer is what XHR returns. */
const previewBlobFromBody = (data: unknown, mediaType: string): Blob => {
  const options = mediaType ? { type: mediaType } : undefined;
  if (data instanceof ArrayBuffer) return new Blob([data], options);
  if (data instanceof Blob) {
    if (mediaType && data.type !== mediaType)
      return new Blob([data], { type: mediaType });
    return data;
  }
  throw new Error('Attachment preview failed');
};

/** Authenticated bytes for a run attachment. Status only: the body can be PHI. */
export const attachmentPreviewBlob = async (
  agentRunId: string,
  attachmentId: string
): Promise<Blob> => {
  let response: ScribeHttpResponse<ArrayBuffer>;
  try {
    response = await http().get<ArrayBuffer>(
      agentHarnessAttachmentContentPath(agentRunId, attachmentId),
      { responseType: 'arraybuffer' }
    );
  } catch (error) {
    throw previewFailure(error);
  }
  return previewBlobFromBody(response.data, mediaTypeFromHeaders(response.headers));
};

export const attachmentDownloadUrl = async (
  agentRunId: string,
  attachmentId: string
): Promise<AttachmentDownloadUrlResponse> => {
  const response = await http().get<AttachmentDownloadUrlResponse>(
    agentHarnessAttachmentDownloadUrlPath(agentRunId, attachmentId)
  );
  return response.data;
};
